'use client';

import { Box, type PopoverOrigin, type PopoverProps } from '@mui/material';
import HoverPopover from 'material-ui-popup-state/HoverPopover';
import { bindFocus, bindHover, bindPopover, usePopupState } from 'material-ui-popup-state/hooks';
import {
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  type KeyboardEventHandler,
  type MouseEventHandler,
  type ReactElement,
  type ReactNode,
} from 'react';

export type HeaderHoverPopoverProps = Omit<PopoverProps, 'content' | 'open'> & {
  id: string;
  content: ReactNode;
  /** 单个可聚焦元素（推荐 type="button" 的 button），将自动合并：点击/键盘先关弹层再执行你传入的 onClick/onKeyDown */
  triggerComponent: ReactElement;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  width?: number | string;
  noPadding?: boolean;
  onOpen?: () => void;
  onOpenChange?: (open: boolean) => void;
};

function chainClick(close: () => void, user?: MouseEventHandler): MouseEventHandler {
  return (e) => {
    close();
    user?.(e);
  };
}

/** 非原生 button（如 div[role=button]）时：Enter/Space 先关弹层，再交给原有 onKeyDown（由业务决定是否模拟点击） */
function mergeKeyDown(close: () => void, user?: KeyboardEventHandler): KeyboardEventHandler {
  return (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      close();
    }
    user?.(e);
  };
}

export default function HeaderHoverPopover({
  id,
  content,
  triggerComponent,
  anchorOrigin = {
    vertical: 'center',
    horizontal: 'right',
  },
  transformOrigin = {
    vertical: 'center',
    horizontal: 'left',
  },
  width = 400,
  noPadding = false,
  onOpen,
  onOpenChange,
  ...restProps
}: HeaderHoverPopoverProps) {
  const popupState = usePopupState({
    variant: 'popover',
    popupId: id,
  });
  const prevIsOpenRef = useRef(popupState.isOpen);

  useEffect(() => {
    if (popupState.isOpen && !prevIsOpenRef.current && onOpen) {
      onOpen();
    }
    prevIsOpenRef.current = popupState.isOpen;
  }, [popupState.isOpen, onOpen]);

  useEffect(() => {
    onOpenChange?.(popupState.isOpen);
  }, [popupState.isOpen, onOpenChange]);

  const hoverFocusProps = {
    ...bindHover(popupState),
    ...bindFocus(popupState),
  };

  const close = () => {
    popupState.close();
  };

  const mergedTrigger = (() => {
    if (!isValidElement(triggerComponent)) {
      return triggerComponent;
    }
    const props = triggerComponent.props as {
      onClick?: MouseEventHandler;
      onKeyDown?: KeyboardEventHandler;
    };
    const isNativeButton = triggerComponent.type === 'button';
    return cloneElement(triggerComponent, {
      onClick: chainClick(close, props.onClick),
      ...(isNativeButton ? {} : { onKeyDown: mergeKeyDown(close, props.onKeyDown) }),
    } as Partial<unknown>);
  })();

  return (
    <>
      <Box {...hoverFocusProps}>{mergedTrigger}</Box>
      <HoverPopover
        {...bindPopover(popupState)}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        marginThreshold={16}
        {...restProps}
      >
        <Box
          style={{
            width: typeof width === 'string' ? width : `${width}px`,
          }}
          className={`popover-content ${noPadding ? '' : 'p-16'}`}
        >
          {content}
        </Box>
      </HoverPopover>
    </>
  );
}
